import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Form, Button, Spinner } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { Megaphone, Link2, MousePointerClick, PlusCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 }
};

const PUBLIC_BASE = (process.env.REACT_APP_PUBLIC_SITE_URL || 'https://resunize.com').replace(
  /\/+$/,
  ''
);

function Advertising() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchLinks = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/advertising/links');
      setLinks(data.links || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load advertising links');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    const slug = newSlug.trim().toLowerCase();
    const display_name = newDisplayName.trim();
    if (!slug || !display_name) {
      toast.error('Enter both a short URL path and a campaign name');
      return;
    }
    setCreating(true);
    try {
      await api.post('/admin/advertising/links', { slug, display_name });
      toast.success('Campaign created');
      setNewSlug('');
      setNewDisplayName('');
      await fetchLinks();
    } catch (err) {
      const msg = err.response?.data?.message || 'Could not create campaign';
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <div className="page-header">
        <h1 className="page-title">Advertising</h1>
        <p className="page-subtitle">
          Short links for podcasts and campaigns. Each visit increments the click counter before
          visitors land on the home page.
        </p>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="glass-card mb-4" style={{ padding: '20px 24px' }}>
        <h3 className="card-title mb-3 d-flex align-items-center gap-2">
          <PlusCircle size={20} className="text-primary" style={{ color: 'var(--accent)' }} />
          New campaign
        </h3>
        <p className="text-muted small mb-3" style={{ maxWidth: 640 }}>
          Choose a short path (letters, numbers, hyphens only). Share{' '}
          <strong>
            {PUBLIC_BASE}/your-path
          </strong>
          . Clicks are counted when someone opens that link, then they are sent to the home page.
        </p>
        <Form onSubmit={handleCreateCampaign}>
          <div className="row g-3">
            <div className="col-md-4">
              <Form.Group>
                <Form.Label>Short path</Form.Label>
                <Form.Control
                  placeholder="e.g. mm, podcast-name"
                  value={newSlug}
                  onChange={(ev) => setNewSlug(ev.target.value)}
                  autoComplete="off"
                  disabled={creating}
                />
                <Form.Text muted>Lowercase, 1–64 characters.</Form.Text>
              </Form.Group>
            </div>
            <div className="col-md-5">
              <Form.Group>
                <Form.Label>Campaign name</Form.Label>
                <Form.Control
                  placeholder="e.g. Meaningful Minute"
                  value={newDisplayName}
                  onChange={(ev) => setNewDisplayName(ev.target.value)}
                  disabled={creating}
                />
                <Form.Text muted>Shown in this table only (for your reference).</Form.Text>
              </Form.Group>
            </div>
            <div className="col-md-3 d-flex align-items-end">
              <Button type="submit" className="w-100" disabled={creating}>
                {creating ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Creating
                  </>
                ) : (
                  'Create campaign'
                )}
              </Button>
            </div>
          </div>
        </Form>
      </div>

      <div className="glass-card" style={{ padding: 0 }}>
        <div
          style={{
            padding: '18px 24px',
            borderBottom: '1px solid var(--border-light)',
            display: 'flex',
            alignItems: 'center',
            gap: 12
          }}
        >
          <span
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              background: 'var(--accent-light)',
              color: 'var(--accent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Megaphone size={20} />
          </span>
          <div>
            <h3 className="card-title" style={{ margin: 0 }}>
              Podcast &amp; campaign links
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
              {links.length} active short path{links.length === 1 ? '' : 's'}
            </p>
          </div>
        </div>

        <div style={{ padding: '0 8px 8px' }}>
          {links.length === 0 ? (
            <p style={{ padding: '24px', color: 'var(--text-muted)', margin: 0 }}>
              No links configured yet.
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="table table-hover mb-0" style={{ fontSize: 14 }}>
                <thead>
                  <tr>
                    <th style={{ borderTop: 'none', paddingLeft: 24 }}>Campaign</th>
                    <th style={{ borderTop: 'none' }}>
                      <span className="d-inline-flex align-items-center gap-1">
                        <Link2 size={14} /> Public URL
                      </span>
                    </th>
                    <th style={{ borderTop: 'none', textAlign: 'right', paddingRight: 24 }}>
                      <span className="d-inline-flex align-items-center gap-1 justify-content-end">
                        <MousePointerClick size={14} /> Clicks
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {links.map((row) => {
                    const url = `${PUBLIC_BASE}/${row.slug}`;
                    return (
                      <tr key={row.slug}>
                        <td style={{ paddingLeft: 24, verticalAlign: 'middle' }}>
                          <div style={{ fontWeight: 600 }}>{row.display_name}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>/{row.slug}</div>
                        </td>
                        <td style={{ verticalAlign: 'middle' }}>
                          <a href={url} target="_blank" rel="noopener noreferrer">
                            {url}
                          </a>
                        </td>
                        <td
                          style={{
                            textAlign: 'right',
                            paddingRight: 24,
                            verticalAlign: 'middle',
                            fontVariantNumeric: 'tabular-nums',
                            fontWeight: 600
                          }}
                        >
                          {Number(row.click_count || 0).toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default Advertising;
